/**
 * FP3-051 follow-up — Solo Provider Workspace Setup Checklist.
 *
 * Shown on the Today dashboard ONLY for providers whose org is
 * `account_type=individual_provider`. Lists 5 setup steps; each
 * already-done step renders as a strikethrough check. Once all
 * 5 are done the widget self-removes — no clutter for
 * established providers.
 *
 * Source of truth is `/api/solo/workspace/setup-checklist`. The
 * widget is small and read-only — no mutation happens here; the
 * CTAs link to the relevant surface (workspace info, students,
 * schedule).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  Circle,
  Sparkle,
  X,
} from "@phosphor-icons/react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local dismissal — once a provider clicks the X they don't see the
// widget again on this device until they re-trigger it (next major
// app version or hard refresh + setItem reset). We use a stable
// localStorage key so the dismissal survives page reloads.
const DISMISS_KEY = "fp3-051-solo-setup-dismissed-v1";

export default function SoloSetupChecklist() {
  // FP3-075E — Simple Mode hides this onboarding widget entirely so
  // a calm Today page never leads with a setup checklist. Advanced
  // Mode keeps it (with working CTAs).
  const { preferences } = useAuth();
  const isSimpleMode = preferences?.ui_mode === "simple";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    if (isSimpleMode) {
      // Don't poll the checklist endpoint when we're never going to
      // render it — save the network round-trip.
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/solo/workspace/setup-checklist`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data || null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSimpleMode]);

  if (isSimpleMode) return null;
  if (loading) return null;
  if (!data) return null;
  if (!data.applicable) return null;
  if (data.complete) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const done = data.steps.filter((s) => s.done).length;
  const total = data.steps.length;

  return (
    <Card
      data-testid="solo-setup-checklist"
      className="bg-gradient-to-br from-[#F7F5EF] to-[#EDEBE2] border border-[#D9D2BD] rounded-2xl shadow-sm mb-6 animate-fade-in"
    >
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <Sparkle size={16} weight="duotone" className="text-[#9C6B2E]" />
            Get your workspace ready
          </CardTitle>
          <p className="text-[11.5px] text-[#6B7280] mt-0.5">
            {done} of {total} steps complete — a few quick moves and you're set.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="solo-setup-checklist-dismiss"
          aria-label="Dismiss checklist"
          className="text-[#9AA0A6] hover:text-[#1F2937] -mr-1"
        >
          <X size={14} weight="bold" />
        </button>
      </CardHeader>
      <CardContent className="space-y-1.5 pt-2">
        {data.steps.map((step) => (
          <ChecklistRow key={step.key} step={step} />
        ))}
      </CardContent>
    </Card>
  );
}

function ChecklistRow({ step }) {
  return (
    <div
      data-testid={`solo-setup-step-${step.key}`}
      data-done={step.done ? "true" : "false"}
      className="flex items-center justify-between gap-3 py-1.5 px-1 rounded-md hover:bg-white/40"
    >
      <div className="flex items-center gap-2 min-w-0">
        {step.done ? (
          <CheckCircle
            size={16}
            weight="fill"
            className="text-[#1E5A3F] flex-shrink-0"
          />
        ) : (
          <Circle size={16} weight="regular" className="text-[#9AA0A6] flex-shrink-0" />
        )}
        <span
          className={`text-[12.5px] ${
            step.done ? "text-[#9AA0A6] line-through" : "text-[#1F2937]"
          }`}
        >
          {step.label}
        </span>
      </div>
      {!step.done && step.cta && step.cta_path && (
        <Link to={step.cta_path} className="flex-shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2.5 text-[11.5px] text-[#1E3F33] hover:bg-white"
          >
            {step.cta}
          </Button>
        </Link>
      )}
    </div>
  );
}
